import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Sidebar.css';

import { API_BASE_URL } from '../config';

function Sidebar({ filters, onFilterChange, onResetFilters }) {
  const location = useLocation();
  const { getAuthHeader } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const isDashboardER = location.pathname === '/er';
  const isDashboardINP = location.pathname === '/inp';
  const isDashboardSNF = location.pathname === '/snf';
  const isDashboardHHA = location.pathname === '/hha';
  const isDashboardHOS = location.pathname === '/hos';
  const isDashboardOP = location.pathname === '/op';
  const isDashboardPHY = location.pathname === '/phy';
  const isDashboardDME = location.pathname === '/dme';
  
  const [filterOptions, setFilterOptions] = useState(
    isDashboardER ? {
      'YR_MO': ['All'],
      'GENDER': ['All'],
      'AVOIDABLE': ['All'],
      'EMERGENT_CAT': ['All'],
      'DISCH_LOC': ['All'],
      'FAC_NAME': ['All'],
      'CLM_BLG_PRVDR_OSCAR_NUM': ['All'],
      'CLM_BLG_PRVDR_NPI_NUM': ['All'],
      'PRNCPL_DGNS_CD': ['All'],
      'CLM_LINE_HCPCS_CD': ['All'],
      'CLM_LINE_PROD_REV_CTR_CD': ['All'],
      'CCSR': ['All'],
      'COHORT': ['All'],
      'CHRONIC': ['All'],
      'READMIT_30D_ER': ['All'],
      'READMIT_30D_INP': ['All'],
      'BENE_MBI_ID': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All']
    } : isDashboardINP ? {
      'BENE_MBI_ID': ['All'],
      'prncpl_dgns_cd': ['All'],
      'admitting_oscar': ['All'],
      'admitting_npi': ['All'],
      'final_drg_cd': ['All'],
      'fac_name': ['All'],
      'yr_mo': ['All'],
      'disch_loc': ['All'],
      'ccsr': ['All'],
      'readmit_30d_flag': ['All'],
      'avoidable': ['All'],
      'CHRONIC': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All'],
      'GENDER': ['All'],
      'COHORT': ['All']
    } : isDashboardSNF || isDashboardHHA ? {
      'COHORT': ['All'],
      'yr_mo': ['All'],
      'CHRONIC': ['All'],
      'GENDER': ['All'],
      'readmit_30d_flag': ['All'],
      'disch_loc': ['All'],
      'fac_name': ['All'],
      'admitting_oscar': ['All'],
      'admitting_npi': ['All'],
      'prncpl_dgns_cd': ['All'],
      'ccsr': ['All'],
      'BENE_MBI_ID': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All']
    } : isDashboardHOS ? {
      'COHORT': ['All'],
      'yr_mo': ['All'],
      'CHRONIC': ['All'],
      'GENDER': ['All'],
      'disch_loc': ['All'],
      'fac_name': ['All'],
      'admitting_oscar': ['All'],
      'admitting_npi': ['All'],
      'prncpl_dgns_cd': ['All'],
      'ccsr': ['All'],
      'BENE_MBI_ID': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All']
    } : isDashboardOP ? {
      'BENE_MBI_ID': ['All'],
      'PRNCPL_DGNS_CD': ['All'],
      'CLM_LINE_HCPCS_CD': ['All'],
      'CLM_LINE_PROD_REV_CTR_CD': ['All'],
      'CLM_BLG_PRVDR_NPI_NUM': ['All'],
      'CLM_BLG_PRVDR_OSCAR_NUM': ['All'],
      'fac_name': ['All'],
      'yr_mo': ['All'],
      'ccsr': ['All'],
      'CHRONIC': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All'],
      'Gender': ['All'],
      'COHORT': ['All']
    } : isDashboardPHY ? {
      'BENE_MBI_ID': ['All'],
      'CLM_LINE_HCPCS_CD': ['All'],
      'PRNCPL_DGNS_CD': ['All'],
      'CLM_RNDRG_PRVDR_TAX_NUM': ['All'],
      'CLM_RNDRG_PRVDR_NPI_NUM': ['All'],
      'yr_mo': ['All'],
      'npi_first_name': ['All'],
      'npi_last_name': ['All'],
      'npi_business_legal_name': ['All'],
      'ccsr': ['All'],
      'CHRONIC': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All'],
      'Gender': ['All'],
      'COHORT': ['All']
    } : isDashboardDME ? {
      'COHORT': ['All'],
      'yr_mo': ['All'],
      'CHRONIC': ['All'],
      'GENDER': ['All'],
      'CLM_LINE_HCPCS_CD': ['All'],
      'npi_business_legal_name': ['All'],
      'CLM_BLG_PRVDR_NPI_NUM': ['All'],
      'BENE_MBI_ID': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All']
    } : {
      'COHORT': ['All'],
      'CHRONIC': ['All'],
      'GENDER': ['All'],
      'BENE_MBI_ID': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All']
    }
  );
  
  const [expandedFilters, setExpandedFilters] = useState({});
  const [searches, setSearches] = useState({});

  useEffect(() => {
    // Initialize expanded filters and searches
    const initialExpanded = {};
    const initialSearches = {};
    Object.keys(filterOptions).forEach(field => {
      initialExpanded[field] = false;
      initialSearches[field] = '';
    });
    setExpandedFilters(initialExpanded);
    setSearches(initialSearches);
    
    // Fetch unique values from BigQuery for dropdowns
    fetchFilterOptions();
  }, [isDashboardER, isDashboardINP, isDashboardSNF, isDashboardHHA, isDashboardHOS, isDashboardOP, isDashboardPHY, isDashboardDME]);

  const fetchFilterOptions = async () => {
    try {
      let endpoint = '/api/filter-options';
      let table = 'DEMOGRAPHICS_PY';
      
      if (isDashboardER) {
        endpoint = '/api/er/filter-options';
        table = 'ER_PY';
      } else if (isDashboardINP) {
        endpoint = '/api/inp/filter-options';
        table = 'INP_PY';
      } else if (isDashboardSNF) {
        endpoint = '/api/snf/filter-options';
        table = 'SNF_PY';
      } else if (isDashboardHHA) {
        endpoint = '/api/hha/filter-options';
        table = 'HHA_PY';
      } else if (isDashboardHOS) {
        endpoint = '/api/hos/filter-options';
        table = 'HOS_PY';
      } else if (isDashboardOP) {
        endpoint = '/api/op/filter-options';
        table = 'OP_PY';
      } else if (isDashboardPHY) {
        endpoint = '/api/phy/filter-options';
        table = 'PHY_PY';
      } else if (isDashboardDME) {
        endpoint = '/api/dme/filter-options';
        table = 'DME_PY';
      }
      
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        params: {
          project: 'crypto-iris-482914-u4',
          dataset: 'Dashboards',
          table: table
        }
      });
      
      if (response.data && response.data.filters) {
        const options = response.data.filters;
        if (isDashboardER) {
          setFilterOptions({
            'YR_MO': ['All', ...(options.yr_mo || [])],
            'GENDER': ['All', ...(options.gender || [])],
            'AVOIDABLE': ['All', ...(options.avoidable || [])],
            'EMERGENT_CAT': ['All', ...(options.emergent_cat || [])],
            'DISCH_LOC': ['All', ...(options.disch_loc || [])],
            'FAC_NAME': ['All', ...(options.fac_name || [])],
            'CLM_BLG_PRVDR_OSCAR_NUM': ['All', ...(options.clm_blg_prvdr_oscar_num || [])],
            'CLM_BLG_PRVDR_NPI_NUM': ['All', ...(options.clm_blg_prvdr_npi_num || [])],
            'BENE_MBI_ID': ['All', ...(options.bene_mbi_id || [])],
            'BENE_1ST_NAME': ['All', ...(options.bene_1st_name || [])],
            'BENE_LAST_NAME': ['All', ...(options.bene_last_name || [])]
          });
        } else if (isDashboardINP) {
          setFilterOptions({
            'BENE_MBI_ID': ['All', ...(options.bene_mbi_id || [])],
            'prncpl_dgns_cd': ['All', ...(options.prncpl_dgns_cd || [])],
            'admitting_oscar': ['All', ...(options.admitting_oscar || [])],
            'admitting_npi': ['All', ...(options.admitting_npi || [])],
            'final_drg_cd': ['All', ...(options.final_drg_cd || [])],
            'fac_name': ['All', ...(options.fac_name || [])],
            'yr_mo': ['All', ...(options.yr_mo || [])],
            'disch_loc': ['All', ...(options.disch_loc || [])],
            'ccsr': ['All', ...(options.ccsr || [])],
            'readmit_30d_flag': ['All', '0', '1'],
            'avoidable': ['All', ...(options.avoidable || ['0', '1'])],
            'CHRONIC': ['All', ...(options.chronic || [])],
            'BENE_1ST_NAME': ['All', ...(options.bene_1st_name || [])],
            'BENE_LAST_NAME': ['All', ...(options.bene_last_name || [])],
            'GENDER': ['All', ...(options.gender || [])],
            'COHORT': ['All', ...(options.cohort || [])]
          });
        } else if (isDashboardOP) {
          setFilterOptions({
            'BENE_MBI_ID': ['All', ...(options.bene_mbi_id || [])],
            'PRNCPL_DGNS_CD': ['All', ...(options.prncpl_dgns_cd || [])],
            'CLM_LINE_HCPCS_CD': ['All', ...(options.clm_line_hcpcs_cd || [])],
            'CLM_LINE_PROD_REV_CTR_CD': ['All', ...(options.clm_line_prod_rev_ctr_cd || [])],
            'CLM_BLG_PRVDR_NPI_NUM': ['All', ...(options.clm_blg_prvdr_npi_num || [])],
            'CLM_BLG_PRVDR_OSCAR_NUM': ['All', ...(options.clm_blg_prvdr_oscar_num || [])],
            'fac_name': ['All', ...(options.fac_name || [])],
            'yr_mo': ['All', ...(options.yr_mo || [])],
            'ccsr': ['All', ...(options.ccsr || [])],
            'CHRONIC': ['All', ...(options.chronic || [])],
            'BENE_1ST_NAME': ['All', ...(options.bene_1st_name || [])],
            'BENE_LAST_NAME': ['All', ...(options.bene_last_name || [])],
            'Gender': ['All', ...(options.gender || [])],
            'COHORT': ['All', ...(options.cohort || [])]
          });
        } else if (isDashboardPHY) {
          setFilterOptions({
            'BENE_MBI_ID': ['All', ...(options.bene_mbi_id || [])],
            'CLM_LINE_HCPCS_CD': ['All', ...(options.clm_line_hcpcs_cd || [])],
            'PRNCPL_DGNS_CD': ['All', ...(options.prncpl_dgns_cd || [])],
            'CLM_RNDRG_PRVDR_TAX_NUM': ['All', ...(options.clm_rndrg_prvdr_tax_num || [])],
            'CLM_RNDRG_PRVDR_NPI_NUM': ['All', ...(options.clm_rndrg_prvdr_npi_num || [])],
            'yr_mo': ['All', ...(options.yr_mo || [])],
            'npi_first_name': ['All', ...(options.npi_first_name || [])],
            'npi_last_name': ['All', ...(options.npi_last_name || [])],
            'npi_business_legal_name': ['All', ...(options.npi_business_legal_name || [])],
            'ccsr': ['All', ...(options.ccsr || [])],
            'CHRONIC': ['All', ...(options.chronic || [])],
            'BENE_1ST_NAME': ['All', ...(options.bene_1st_name || [])],
            'BENE_LAST_NAME': ['All', ...(options.bene_last_name || [])],
            'Gender': ['All', ...(options.gender || [])],
            'COHORT': ['All', ...(options.cohort || [])]
          });
        } else if (isDashboardDME) {
          setFilterOptions({
            'BENE_MBI_ID': ['All', ...(options.bene_mbi_id || [])],
            'CLM_LINE_HCPCS_CD': ['All', ...(options.clm_line_hcpcs_cd || [])],
            'CLM_BLG_PRVDR_NPI_NUM': ['All', ...(options.clm_blg_prvdr_npi_num || [])],
            'yr_mo': ['All', ...(options.yr_mo || [])],
            'npi_business_legal_name': ['All', ...(options.npi_business_legal_name || [])],
            'CHRONIC': ['All', ...(options.chronic || [])],
            'BENE_1ST_NAME': ['All', ...(options.bene_1st_name || [])],
            'BENE_LAST_NAME': ['All', ...(options.bene_last_name || [])],
            'GENDER': ['All', ...(options.gender || [])],
            'COHORT': ['All', ...(options.cohort || [])]
          });
        } else if (isDashboardSNF || isDashboardHHA) {
          setFilterOptions({
            'BENE_MBI_ID': ['All', ...(options.bene_mbi_id || [])],
            'prncpl_dgns_cd': ['All', ...(options.prncpl_dgns_cd || [])],
            'admitting_oscar': ['All', ...(options.admitting_oscar || [])],
            'admitting_npi': ['All', ...(options.admitting_npi || [])],
            'fac_name': ['All', ...(options.fac_name || [])],
            'yr_mo': ['All', ...(options.yr_mo || [])],
            'disch_loc': ['All', ...(options.disch_loc || [])],
            'ccsr': ['All', ...(options.ccsr || [])],
            'readmit_30d_flag': ['All', '0', '1'],
            'CHRONIC': ['All', ...(options.chronic || [])],
            'GENDER': ['All', ...(options.gender || [])],
            'COHORT': ['All', ...(options.cohort || [])],
            'BENE_1ST_NAME': ['All', ...(options.bene_1st_name || [])],
            'BENE_LAST_NAME': ['All', ...(options.bene_last_name || [])]
          });
        } else if (isDashboardHOS) {
          setFilterOptions({
            'BENE_MBI_ID': ['All', ...(options.bene_mbi_id || [])],
            'prncpl_dgns_cd': ['All', ...(options.prncpl_dgns_cd || [])],
            'admitting_oscar': ['All', ...(options.admitting_oscar || [])],
            'admitting_npi': ['All', ...(options.admitting_npi || [])],
            'fac_name': ['All', ...(options.fac_name || [])],
            'yr_mo': ['All', ...(options.yr_mo || [])],
            'disch_loc': ['All', ...(options.disch_loc || [])],
            'ccsr': ['All', ...(options.ccsr || [])],
            'CHRONIC': ['All', ...(options.chronic || [])],
            'GENDER': ['All', ...(options.gender || [])],
            'COHORT': ['All', ...(options.cohort || [])],
            'BENE_1ST_NAME': ['All', ...(options.bene_1st_name || [])],
            'BENE_LAST_NAME': ['All', ...(options.bene_last_name || [])]
          });
        } else {
          setFilterOptions({
            'BENE_MBI_ID': ['All', ...(options.BENE_MBI_ID || [])],
            'BENE_1ST_NAME': ['All', ...(options.BENE_1ST_NAME || [])],
            'BENE_LAST_NAME': ['All', ...(options.BENE_LAST_NAME || [])],
            'GENDER': ['All', ...(options.GENDER || [])],
            'COUNTY': ['All', ...(options.COUNTY || [])],
            'COHORT': ['All', ...(options.COHORT || [])],
            'CHRONIC': ['All', ...(options.CHRONIC || [])],
            'DEATH': ['All', '1', '0'],
            'STATE': ['All', ...(options.STATE || [])]
          });
        }
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Keep default options if API call fails
    }
  };

  const handleCheckboxChange = (field, option) => {
    const currentSelections = filters[field] || ['All'];
    let newSelections;

    if (option === 'All') {
      newSelections = ['All'];
    } else {
      if (currentSelections.includes('All')) {
        newSelections = [option];
      } else if (currentSelections.includes(option)) {
        newSelections = currentSelections.filter(v => v !== option);
        if (newSelections.length === 0) {
          newSelections = ['All'];
        }
      } else {
        newSelections = [...currentSelections, option];
      }
    }

    onFilterChange(field, newSelections);
  };

  const toggleFilterExpand = (field) => {
    setExpandedFilters(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const updateSearch = (field, value) => {
    setSearches(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filterOrder = isDashboardER ? 
    ['AVOIDABLE', 'EMERGENT_CAT', 'DISCH_LOC', 'FAC_NAME', 'CLM_BLG_PRVDR_OSCAR_NUM', 'CLM_BLG_PRVDR_NPI_NUM', 'YR_MO', 'BENE_MBI_ID', 'BENE_1ST_NAME', 'BENE_LAST_NAME', 'GENDER'] :
    isDashboardINP ? 
    ['BENE_MBI_ID', 'prncpl_dgns_cd', 'admitting_oscar', 'admitting_npi', 'final_drg_cd', 'fac_name', 'yr_mo', 'disch_loc', 'ccsr', 'readmit_30d_flag', 'avoidable', 'CHRONIC', 'BENE_1ST_NAME', 'BENE_LAST_NAME', 'GENDER', 'COHORT'] :
    (isDashboardSNF || isDashboardHHA) ?
    ['BENE_MBI_ID', 'prncpl_dgns_cd', 'admitting_oscar', 'admitting_npi', 'fac_name', 'yr_mo', 'disch_loc', 'ccsr', 'readmit_30d_flag', 'CHRONIC', 'BENE_1ST_NAME', 'BENE_LAST_NAME', 'GENDER', 'COHORT'] :
    isDashboardHOS ?
    ['BENE_MBI_ID', 'prncpl_dgns_cd', 'admitting_oscar', 'admitting_npi', 'fac_name', 'yr_mo', 'disch_loc', 'ccsr', 'CHRONIC', 'BENE_1ST_NAME', 'BENE_LAST_NAME', 'GENDER', 'COHORT'] :
    isDashboardOP ?
    ['BENE_MBI_ID', 'PRNCPL_DGNS_CD', 'CLM_LINE_HCPCS_CD', 'CLM_LINE_PROD_REV_CTR_CD', 'CLM_BLG_PRVDR_NPI_NUM', 'CLM_BLG_PRVDR_OSCAR_NUM', 'fac_name', 'yr_mo', 'ccsr', 'CHRONIC', 'BENE_1ST_NAME', 'BENE_LAST_NAME', 'Gender', 'COHORT'] :
    isDashboardPHY ?
    ['BENE_MBI_ID', 'CLM_LINE_HCPCS_CD', 'PRNCPL_DGNS_CD', 'CLM_RNDRG_PRVDR_TAX_NUM', 'CLM_RNDRG_PRVDR_NPI_NUM', 'yr_mo', 'npi_first_name', 'npi_last_name', 'npi_business_legal_name', 'ccsr', 'CHRONIC', 'BENE_1ST_NAME', 'BENE_LAST_NAME', 'Gender', 'COHORT'] :
    isDashboardDME ?
    ['BENE_MBI_ID', 'CLM_LINE_HCPCS_CD', 'CLM_BLG_PRVDR_NPI_NUM', 'yr_mo', 'npi_business_legal_name', 'CHRONIC', 'BENE_1ST_NAME', 'BENE_LAST_NAME', 'GENDER', 'COHORT'] :
    ['BENE_MBI_ID', 'BENE_1ST_NAME', 'BENE_LAST_NAME', 'GENDER', 'COHORT', 'CHRONIC'];

  const searchableFields = ['BENE_MBI_ID', 'BENE_1ST_NAME', 'BENE_LAST_NAME', 'CHRONIC', 'FAC_NAME', 'EMERGENT_CAT', 'DISCH_LOC', 'CLM_BLG_PRVDR_OSCAR_NUM', 'CLM_BLG_PRVDR_NPI_NUM', 'YR_MO', 'fac_name', 'prncpl_dgns_cd'];

  // Determine if a field should have search - either manually in searchableFields or if it has more than 7 options
  const shouldShowSearchBar = (field) => {
    if (searchableFields.includes(field)) {
      return true;
    }
    const optionCount = (filterOptions[field] || []).length;
    return optionCount > 7;
  };

  const handleResetFilters = () => {
    if (onResetFilters) {
      onResetFilters();
    }
  };

  return (
    <>
      {/* Mobile FAB: tap to open filters */}
      <button
        className="sidebar-mobile-toggle"
        onClick={() => setIsMobileOpen(true)}
        aria-label="Open filters"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="4" y1="6"  x2="20" y2="6"  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="4" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="4" y1="18" x2="12" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        Filters
      </button>

      {/* Dark backdrop when sidebar is open on mobile */}
      {isMobileOpen && (
        <div className="sidebar-backdrop" onClick={() => setIsMobileOpen(false)} />
      )}

      <aside className={`sidebar${isMobileOpen ? ' sidebar--mobile-open' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '8px' }}>
          <h3 className="sidebar-title">FILTERS</h3>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button
              onClick={handleResetFilters}
              style={{
                padding: '6px 12px',
                backgroundColor: '#a5d6a7',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: '600',
                color: '#2e7d32',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                marginTop: '-20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#4caf50';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#a5d6a7';
                e.target.style.color = '#2e7d32';
              }}
            >
              Reset
            </button>
            <button
              className="sidebar-close-btn"
              onClick={() => setIsMobileOpen(false)}
              aria-label="Close filters"
            >
              ✕
            </button>
          </div>
        </div>
      <div className="filter-list">
        {Object.entries(filterOptions).sort(([keyA], [keyB]) => {
          return filterOrder.indexOf(keyA) - filterOrder.indexOf(keyB);
        }).map(([field, options]) => (
          // SUPER USER GATE: BENE_MBI_ID disabled until role-based access is implemented
          <div key={field} className="filter-item" style={field === 'BENE_MBI_ID' ? {pointerEvents: 'none', opacity: 0.35, userSelect: 'none'} : {}}>
            <div 
              className="filter-header"
              onClick={() => toggleFilterExpand(field)}
              style={{
                cursor: 'pointer',
                fontWeight: 'bold',
                padding: '8px 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <span>{field.replace('_', ' ')}</span>
              <span style={{fontSize: '12px'}}>{expandedFilters[field] ? '▼' : '▶'}</span>
            </div>
            {expandedFilters[field] && (
              <>
                {shouldShowSearchBar(field) && (
                  <input
                    type="text"
                    placeholder={`Search ${field.toLowerCase().replace(/_/g, ' ')}...`}
                    value={searches[field] || ''}
                    onChange={(e) => updateSearch(field, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      marginTop: '8px',
                      marginBottom: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <div className="checkbox-list" style={{maxHeight: '200px', overflowY: 'auto', marginTop: shouldShowSearchBar(field) ? '0' : '8px'}}>
                  {options
                    .filter(option => {
                      if (shouldShowSearchBar(field)) {
                        if (option === 'All') return true;
                        return option.toLowerCase().includes((searches[field] || '').toLowerCase());
                      }
                      return true;
                    })
                    .map((option) => {
                      const isChecked = (filters[field] || ['All']).includes(option);
                      return (
                        <label 
                          key={option} 
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px 0',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleCheckboxChange(field, option)}
                            style={{marginRight: '8px', cursor: 'pointer'}}
                          />
                          {option}
                        </label>
                      );
                    })}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      </aside>
    </>
  );
}

export default Sidebar;
